import { RequireAuth, useFusionAuth } from "@fusionauth/react-sdk";
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import { Stack } from "@mui/system";
import React, { useContext, useEffect, useState } from "react";
import CloseArrowTooltip from "../../../components/CloseArrowTooltip";
import CustomDialog from "../../../components/CustomDialog";
import {
  ProjectContext,
  ProjectDispatchContext,
} from "../../../contexts/ProjectContext";
import {
  convertProjectContextToProject,
  convertProjectToProjectContext,
} from "../../../models/Project";
import { defaultSource } from "../../../models/Source";
import { useFetch } from "../../../services/hooks/useFetch";
import { apiPrefix, deviceApiPrefix, sourceAsText } from "../../../utils/main";

interface ProjectCopyRequest {
  oldProjectId: string;
  oldSchema: string;
  newProjectSourceId: string;
  newSchema: string;
  newProjectName: string;
}

export default function ProjectDetailsSettingsPage() {
  const [editMode, setEditMode] = useState(false);
  const [refreshValues, setRefreshValues] = useState(false);

  const project = useContext(ProjectContext)!;
  const dispatch = useContext(ProjectDispatchContext);
  const { update: updateProject } = useFetch<
    undefined,
    ProjectRequest,
    Project
  >();
  const { update: copyProject } = useFetch<undefined, ProjectCopyRequest>();

  const [projectStatus, setProjectStatus] = useState(true);
  const [projectName, setProjectName] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const { data: source = defaultSource, fetch: getSource } = useFetch<Source>();
  const { data: validSources, fetch: getSources } = useFetch<Source[]>();
  const [updateReason, setUpdateReason] = useState("");

  const [displayErrors, setDisplayErrors] = useState(false);

  const projectSource = {
    id: project.sourceId!,
    schemaIdentifier: project.sourceSchema!,
    name: "",
    databaseName: "",
    availableForNewProjects: true,
    datasetCutoffDate: "",
  };

  const [copyProjectName, setCopyProjectName] = useState("");
  const [copyProjectDatasource, setCopyProjectDatasource] =
    useState<Source>(projectSource);
  const [displayCopyErrors, setDisplayCopyErrors] = useState(false);
  const [displayCopyDialog, setDisplayCopyDialog] = useState(false);

  const { userInfo } = useFusionAuth();
  const isAdmin = userInfo?.roles?.includes("ADMIN");

  // Refresh the component states from the project values
  useEffect(() => {
    if (project) {
      if (typeof project.isActive === "boolean") {
        setProjectStatus(project.isActive);
      }

      setProjectName(project.name);

      if (typeof project.description === "string") {
        setProjectDescription(project.description);
      }

      if (isAdmin) {
        void getSource(`${deviceApiPrefix}/sources/${project.sourceId}`);
        void getSources(`${deviceApiPrefix}/sources/`);
      }
    }
    if (refreshValues) setRefreshValues(false);
  }, [refreshValues, project, getSource, isAdmin, getSources]);

  const handleUpdates = function () {
    if (projectName !== "" && updateReason !== "") {
      project.isActive = projectStatus;
      project.name = projectName;
      project.description = projectDescription;

      void updateProject({
        path: `${apiPrefix}/projects/${project.id}`,
        body: {
          reason: updateReason,
          project: convertProjectContextToProject(project),
        },
        responseManipulationFunction: (data) => {
          convertProjectToProjectContext(data, dispatch);
          return;
        },
        postResponseAction: () => {
          setRefreshValues(true);
          setEditMode(false);
          setDisplayErrors(false);
        },
      });
    } else {
      setDisplayErrors(true);
    }
  };

  function onSourceChange(event: SelectChangeEvent) {
    if (!validSources) return;
    const selectedSources = validSources.filter(
      (source: Source) => source.id === event.target.value,
    );
    if (selectedSources.length > 0) {
      setCopyProjectDatasource(selectedSources[0]);
    } else {
      setCopyProjectDatasource(projectSource);
    }
  }

  function closeCopyDialog() {
    setDisplayCopyDialog(false);
    setDisplayCopyErrors(false);
    setCopyProjectName("");
  }

  const handleCopy = function () {
    if (copyProjectName.trim() === "") {
      setDisplayCopyErrors(true);
      return;
    }

    void copyProject({
      path: `${apiPrefix}/projects/${project.id}/copy`,
      body: {
        oldProjectId: project.id!,
        oldSchema: project.sourceSchema!,
        newProjectSourceId: copyProjectDatasource.id,
        newSchema: copyProjectDatasource.schemaIdentifier,
        newProjectName: copyProjectName,
      },
      postResponseAction: () => {
        closeCopyDialog();
      },
    });
  };

  return (
    <Grid container direction={"column"} className="company-settings-item">
      <div className="company-settings-item">
        <Grid>
          <Typography variant="h4">Project Details</Typography>
        </Grid>
        <br />
        <Grid>
          <Typography variant="body2">
            The name and description for this project. This information can be
            modified at any time after the project is created.
          </Typography>
        </Grid>
        <br />
        <Grid>
          <Typography variant="body2">* Required fields</Typography>
        </Grid>
        <br />
        <RequireAuth withRole={"ADMIN"}>
          <Grid>
            {editMode ? (
              <>
                <InputLabel shrink htmlFor="select-project-status" required>
                  Project Status
                </InputLabel>
                <Select
                  required
                  inputProps={{ id: "select-project-status" }}
                  sx={{ width: "50ch" }}
                  value={projectStatus}
                  onChange={(event) => {
                    setProjectStatus(event.target.value === "true");
                  }}
                  readOnly={!editMode}
                  variant="outlined"
                >
                  <MenuItem value={"true"}>Active</MenuItem>
                  <MenuItem value={"false"}>Inactive</MenuItem>
                </Select>
              </>
            ) : (
              <>
                <Typography variant="caption">Project Status</Typography>
                <Typography variant="body1" sx={{ width: "50ch" }}>
                  {projectStatus ? "Active" : "Inactive"}
                </Typography>
              </>
            )}
          </Grid>
        </RequireAuth>
        <br />
        <Grid>
          {editMode ? (
            <TextField
              required
              label={"Project Name"}
              sx={{ width: "50ch" }}
              value={projectName}
              error={displayErrors && projectName === ""}
              helperText={
                displayErrors && projectName === ""
                  ? "This field is required"
                  : ""
              }
              onChange={(event) => {
                setProjectName(event.target.value);
              }}
              slotProps={{
                input: {
                  readOnly: !editMode,
                },
              }}
            ></TextField>
          ) : (
            <>
              <Typography variant="caption">Project Name *</Typography>
              <Typography variant="body1" sx={{ width: "50ch" }}>
                {projectName}
              </Typography>
            </>
          )}
        </Grid>
        <br />
        <Grid>
          {editMode ? (
            <TextField
              multiline
              label={"Project Description"}
              sx={{ width: "50ch" }}
              value={projectDescription}
              onChange={(event) => {
                setProjectDescription(event.target.value);
              }}
              slotProps={{
                input: {
                  readOnly: !editMode,
                },
              }}
            ></TextField>
          ) : (
            <>
              <Typography variant="caption">Project Description</Typography>
              <Typography variant="body1" sx={{ width: "50ch" }}>
                {projectDescription}
              </Typography>
            </>
          )}
        </Grid>
        <br />
        <RequireAuth withRole={"ADMIN"}>
          <Grid>
            <Typography variant="caption">Data Source</Typography>
            <Typography variant="body1" sx={{ width: "50ch" }}>
              {sourceAsText(source)}
            </Typography>
          </Grid>
        </RequireAuth>

        {editMode && (
          <div>
            <br />
            <Grid>
              <TextField
                required
                label="Note/Reason for Updating the Project Details"
                sx={{ width: "50ch" }}
                value={updateReason}
                error={displayErrors && updateReason === ""}
                helperText={
                  displayErrors && updateReason === ""
                    ? "This field is required"
                    : ""
                }
                onChange={(event) => {
                  setUpdateReason(event.target.value);
                }}
              ></TextField>
            </Grid>
          </div>
        )}
        <br />
        <Grid container justifyContent={"right"}>
          {(editMode && (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  setEditMode(false);
                  setRefreshValues(true);
                  setDisplayErrors(false);
                  setUpdateReason("");
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                className="update-button"
                sx={{ marginLeft: "5px" }}
                onClick={handleUpdates}
              >
                Update
              </Button>
            </>
          )) || (
            <>
              <RequireAuth withRole={"ADMIN"}>
                <Button
                  sx={{ marginRight: "5px" }}
                  variant="contained"
                  onClick={() => setDisplayCopyDialog(true)}
                >
                  Copy
                </Button>
              </RequireAuth>
              <CloseArrowTooltip
                title={project.isLocked ? "This project is locked." : ""}
              >
                <span>
                  <Button
                    variant="contained"
                    onClick={() => setEditMode(true)}
                    disabled={project.isLocked}
                  >
                    Edit
                  </Button>
                </span>
              </CloseArrowTooltip>
            </>
          )}
        </Grid>
      </div>
      <CustomDialog
        open={displayCopyDialog}
        title={`Copy ${projectName}?`}
        handleClose={closeCopyDialog}
        hasConfirmation
        confirmationText={`Copy`}
        handleConfirm={handleCopy}
      >
        <Stack>
          <TextField
            required
            label={"Copied Project Name"}
            sx={{ width: "50ch" }}
            value={copyProjectName}
            error={displayCopyErrors && copyProjectName === ""}
            helperText={
              displayCopyErrors && copyProjectName === ""
                ? "This field is required"
                : ""
            }
            onChange={(event) => {
              setCopyProjectName(event.target.value);
            }}
          />
          <br />
          <FormControl fullWidth>
            <InputLabel
              id="select-source-label"
              required
              error={displayCopyErrors && !copyProjectDatasource}
            >
              New Project Data Source
            </InputLabel>
            <Select
              required
              label={"New Project Data Source"}
              labelId="select-source-label"
              sx={{ bgcolor: "common.white" }}
              onChange={onSourceChange}
              value={copyProjectDatasource.id}
              inputProps={{ id: "select-source", sx: { fontSize: 14 } }}
              variant="outlined"
              error={displayCopyErrors && !copyProjectDatasource}
            >
              {validSources &&
                validSources.map((source: Source) => {
                  return (
                    <MenuItem
                      key={`${source.name}_${source.databaseName}`}
                      value={source.id}
                    >
                      {sourceAsText(source)}
                    </MenuItem>
                  );
                })}
            </Select>
          </FormControl>
        </Stack>
      </CustomDialog>
    </Grid>
  );
}
